import React, { useRef } from "react";
import PropTypes from "prop-types";
import Image from "../global/Image";
import SmartColour from "../../utils/ColourHandler";
import SmartText from "../../utils/TextHandler";
import SmartStyles from "../../utils/StyleHandler";
import SectionNumber from "../global/SectionNumber";
import useWindowSize from "../../utils/ScreenHelper";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";

/********************************
<Text />
/********************************
Version: 1.2
Created: 11 08 2023
Updated: 20 01 2024
Author: Richy Pears
/********************************
A section wrapper for text
/********************************/

const Text = ({ blok }) => {
  const window = useWindowSize();

  const {
    align,
    aspect,
    copy,
    image,
    padding,
    rounded,
    theme,
    title,
    title_size,
    subtitle,
    number,
  } = blok;

  const colour = SmartColour(theme);

  const container = useRef();
  const media = useRef();
  const text = useRef();

  useGSAP(
    () => {
      var mediaTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 100%",
          end: "center 50%",
          scrub: false,
        },
      });

      mediaTimeline.from(text.current, {
        opacity: 0,
      });
      mediaTimeline.from(media.current, {
        duration: 2.5,
        x: align === "left" ? "-100px" : align === "right" ? "100px" : 0,
        opacity: 0,
      });
      mediaTimeline.to(media.current, {
        duration: 2.5,
        x: 0,
        opacity: 1,
      });
    },
    { scope: container.current }
  );

  if (!blok) return null; // Escape hatch

  return (
    <section
      className={`flex flex-col ${
        colour.classes && colour.classes
      } ${SmartStyles("padding", padding)} ${SmartStyles("rounded", rounded)}`}
      ref={container}
    >
      <div className="brand-container relative">
        <div
          className={`flex flex-col ${
            align === "left"
              ? "md:flex-row-reverse"
              : align === "right"
              ? "md:flex-row"
              : ""
          } justify-between align-center gap-10 md:gap-16 items-center relative`}
        >
          <div
            className={`flex flex-col gap-10 md:gap-12 w-full grow-0 shrink-0 justify-center ${
              align === "center" ? "" : "md:w-5/12"
            }`}
            ref={text}
          >
            {window.width < 768 || align === "center" ? (
              <SectionNumber
                title={subtitle && subtitle}
                number={number && number}
              />
            ) : align === "right" ? (
              // Image is on the right, so show the title here
              <SectionNumber title={subtitle && subtitle} />
            ) : (
              // Image is on the left so show the number
              <SectionNumber number={number && number} />
            )}
            {title && title_size === "h3" ? (
              <h3
                className={`mb-0 text-center ${
                  align !== "center" ? "md:text-left" : ""
                }`}
              >
                {title}
              </h3>
            ) : title_size === "h2" ? (
              <h2
                className={`mb-0 text-center ${
                  align !== "center" ? "md:text-left" : ""
                }`}
              >
                {title}
              </h2>
            ) : (
              <h1
                className={`mb-0 text-center ${
                  align !== "center" ? "md:text-left" : ""
                }`}
              >
                {title}
              </h1>
            )}
            {copy && (
              <SmartText
                className={`m-0 brand-remove-last-margin hidden ${
                  align !== "center" ? "md:block" : ""
                }`}
              >
                {copy}
              </SmartText>
            )}
          </div>
          <div
            className={`flex flex-col w-full ${
              align !== "center" ? "md:w-8/12" : ""
            } grow-0 shrink-0 h-min relative`}
          >
            {window.width >= 768 && align !== "center" ? (
              align === "right" ? (
                // Image is on the right, so show the number here
                <SectionNumber
                  number={number && number}
                  css="absolute right-10 top-10 text-white mix-blend-difference w-min"
                />
              ) : (
                // Image is on the left so show the title
                <SectionNumber
                  title={subtitle && subtitle}
                  css="absolute left-10 top-10 text-white mix-blend-difference w-min"
                />
              )
            ) : null}
            {image && (
              <Image
                src={image}
                className="rounded-3xl"
                aspect={aspect ? aspect : null}
                ref={media}
              />
            )}
          </div>
          {copy && (
            <SmartText
              className={`m-0 brand-remove-last-margin block ${
                align !== "center" ? "md:hidden" : ""
              } text-center`}
            >
              {copy}
            </SmartText>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <section
      className={`flex flex-col ${
        colour.classes && colour.classes
      } ${SmartStyles("padding", padding)} ${SmartStyles("rounded", rounded)}`}
    >
      <div className="brand-container relative">
        <div className={`relative flex flex-row justify-end`}>
          <div
            className={`absolute top-1/2 left-0 right-0 border border-green -translate-y-1/2 ${
              !subtitle && !number ? null : "-mt-10"
            }`}
          >
            <SectionNumber
              title={subtitle && subtitle}
              number={number && number}
              css="mb-20"
            />
            {title && <h1 className="mb-10">{title}</h1>}
            {copy && (
              <SmartText className="m-0 brand-remove-last-margin w-5/12">
                {copy}
              </SmartText>
            )}
          </div>
          <div className="flex flex-col w-8/12 grow-0 shrink-0 -me-36">
            {image && (
              <Image
                src={image}
                className="rounded-3xl"
                aspect={aspect ? aspect : null}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <section
      className={`flex flex-col ${
        colour.classes && colour.classes
      } ${SmartStyles("padding", padding)} ${SmartStyles("rounded", rounded)}`}
    >
      <div className="brand-container relative">
        <div
          className={`flex ${
            align === "right" ? "flex-row" : "flex-row-reverse"
          } justify-between align-center gap-12`}
        >
          <div className="flex flex-col gap-5 md:gap-10 w-5/12 grow-0 shrink-0 justify-center">
            {copy && (
              <SmartText className="m-0 brand-remove-last-margin">
                {copy}
              </SmartText>
            )}
          </div>
          <div className="flex flex-col w-8/12 grow-0 shrink-0">
            {image && (
              <Image
                src={image}
                className="rounded-3xl"
                aspect={aspect ? aspect : null}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Text;

Text.defaultProps = {
  blok: null,
};

Text.propTypes = {
  blok: PropTypes.object,
};
